// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

/**
 * Bounds how many photo-byte requests are in flight at once.
 *
 * On web the gallery cannot use a plain `<Image>` — the byte route needs an Authorization header,
 * which react-native-web drops — so every tile issues its own `fetch`. Without a limit a screenful
 * fires all of them simultaneously, and two things go wrong:
 *
 * - browsers cap concurrent connections per origin (~6), so the surplus queues at the socket layer
 *   where a slow or overloaded server can drop it outright rather than answering late;
 * - each request makes the BFF open and stream a separate S3 object, so a 200-photo tour would ask
 *   it for 200 concurrent streams.
 *
 * Four at a time keeps the grid filling visibly fast while leaving connections for the rest of the
 * app (the WebSocket, the leaderboard polling) and staying well inside the per-origin cap.
 */
const MAX_CONCURRENT = 4;

let active = 0;
const waiting: (() => void)[] = [];

const acquire = (): Promise<void> => {
  if (active < MAX_CONCURRENT) {
    active += 1;
    return Promise.resolve();
  }

  return new Promise<void>((resolve) => {
    waiting.push(() => {
      active += 1;
      resolve();
    });
  });
};

const release = () => {
  active = Math.max(0, active - 1);
  const next = waiting.shift();
  if (next) {
    next();
  }
};

/**
 * Runs `task` once a slot is free. The slot is always released, including on rejection, so one
 * failed tile cannot wedge the rest of the grid.
 */
export const withImageFetchSlot = async <T,>(task: () => Promise<T>): Promise<T> => {
  await acquire();
  try {
    return await task();
  } finally {
    release();
  }
};

/** Test seam: asserts the queue drains rather than leaking slots. */
export const __imageFetchQueueState = () => ({ active, waiting: waiting.length });
